import { useMutation, useQuery, useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";

export interface JsonSlotMeta {
  filename: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

/**
 * Shared upload/download logic for a single JSON slot — extracted out of `JsonSlotCard.tsx` so
 * both that per-plugin card AND the flat `/config/documents` list row
 * (`ConfigDocumentsListScreen.tsx`) call the exact same PUT/GET code, no duplicated handling.
 *
 * There's no wrapper on `ApiService.fetch` for file bytes (it always JSON-encodes the body and
 * JSON-decodes the response — see that file), so upload reads the selected `File` as text and
 * PUTs `{ filename, content: <raw text> }` (still a normal JSON body; `content` just happens to
 * be a JSON-text string the server parses/validates), and download uses a plain `fetch()` + Blob
 * with `credentials: "include"` for the same httpOnly-cookie auth `ApiService.fetch` would have
 * sent (GET, so no `X-Requested-With` needed — that's only required on mutating routes).
 *
 * `initialData`: the Documents tab's bulk `GET /config/json-slots` fetch already returns every
 * row's filename/updatedAt/updatedBy in one call — a caller that already has that (
 * `ConfigDocumentsListScreen.tsx`) passes it here so this hook's own per-slot GET is seeded
 * instead of re-fetched (code-review follow-up: without this, every row re-issued the exact GET
 * the bulk endpoint had just made redundant, an N+1 the moment a second plugin declares a slot).
 * `staleTime: Infinity` on a seeded query means it's never considered stale on its own — an
 * `invalidateQueries` call (e.g. after an upload) still forces a refetch regardless of staleTime.
 */
export function useJsonSlot(slotKey: string, initialData?: JsonSlotMeta) {
  const queryClient = useQueryClient();
  const queryKey = ["jsonSlot", slotKey];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await ApiService.fetch<{ data: JsonSlotMeta }>(
        "GET",
        `/api/config/json-slots/${slotKey}`
      );
      return response.data;
    },
    ...(initialData ? { initialData, staleTime: Infinity } : {}),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const content = await file.text();
      await ApiService.fetch("PUT", `/api/config/json-slots/${slotKey}`, {
        filename: file.name,
        content,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // The Documents tab's bulk list (`jsonSlots`) and this slot's own per-plugin card share
      // the same underlying row — invalidate both so an upload from either surface is
      // immediately reflected on the other without a manual refresh.
      queryClient.invalidateQueries({ queryKey: ["jsonSlots"] });
    },
  });

  const download = async () => {
    const response = await fetch(
      `${ApiService.apiUrl}/api/config/json-slots/${slotKey}/download`,
      { credentials: "include" }
    );
    const blob = await response.blob();
    // Backend escapes `"`/`\` with a backslash before quoting (jsonSlots.ts's
    // escapeContentDispositionFilename) — match through escaped chars, then undo the escaping.
    const match = (response.headers.get("content-disposition") ?? "").match(
      /filename="((?:[^"\\]|\\.)*)"/
    );
    const filename = match
      ? match[1].replace(/\\(.)/g, "$1")
      : `${slotKey}.json`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return {
    data,
    isLoading,
    download,
    upload: uploadMutation.mutate,
    isUploading: uploadMutation.isLoading,
    uploadError: uploadMutation.isError
      ? (uploadMutation.error as Error)?.message ||
        "Upload failed — is the file valid JSON?"
      : undefined,
  };
}

export default useJsonSlot;
